import React, { useEffect, useState, useRef } from "react";
import { keyConstants, uiConstant } from "../../constants";
import PopupComponent from "../../modules/dialog/addUrlPopup";
import ToggleSwitch from "../../common/components/toggleSwitch";
import { hideLoader, removeUser, showLoader } from "../../slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utility";
import { BusinessUser } from "../../services";
import { SketchPicker } from "react-color";
import axios from "axios";
import PropTypes from "prop-types";

const WidgetComponent = ({
  setDefaultWidgetSettings,
  selectedWidgetType,
  setSelectedWidgetType,
  themeColors,
  setThemeColors,
  selectedFont,
  setSelectedFont,
  adUnitCode,
  setAdUnitCode,
  showAdWidget,
  setShowAdWidget
}) => {
  const businessID = useSelector((state) => state?.user?._id);
  const dispatch = useDispatch();
  const colorPickerRef = useRef(null);
  const fontDropdownRef = useRef(null);

  const { widgetType } = uiConstant.widgetData;
  const [showPopup, setShowPopup] = useState(false);
  const [selectedThemeIndex, setSelectedThemeIndex] = useState(null);
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [fonts, setFonts] = useState([]);
  const [visibleFonts, setVisibleFonts] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;
  const fetchFonts = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_GOOGLE_WEBFONTS_URL
      );
      setFonts(response.data.items);
    } catch (error) {
      console.error("Error fetching fonts:", error);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop === clientHeight) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    fetchFonts();
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setSelectedThemeIndex(null); // Close color picker
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (
        fontDropdownRef.current &&
        !fontDropdownRef.current.contains(event.target)
      ) {
        setIsFontDropdownOpen(false); // Close font dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutsideDropdown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, []);

  useEffect(() => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    setVisibleFonts(fonts.slice(0, endIndex));
  }, [fonts, page, perPage]);

  useEffect(() => {
    const fetchWidgetSettings = async () => {
      const tokenStatus =
        await Utils.checkTokenExpireOrNot(fetchWidgetSettings);
      if (tokenStatus) {
        if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
          dispatch(showLoader());
          return;
        }
        dispatch(removeUser({}));
        return;
      }
      try {
        dispatch(showLoader());
        const businessDetails = await new BusinessUser().getBusiness(
          businessID
        );

        if (
          businessDetails &&
          businessDetails.settings &&
          businessDetails.settings.widgetSettings
        ) {
          const { type, theme, font, showAdWidget, adUnitCode } =
            businessDetails.settings.widgetSettings;
          setSelectedWidgetType({ index: null, value: type });
          setThemeColors([
            { name: "Background", color: theme.backgroundColor },
            { name: "Font", color: theme.fontColor },
            { name: "Button", color: theme.buttonColor }
          ]);

          setSelectedFont(font);
          setShowAdWidget(showAdWidget);
          setAdUnitCode(adUnitCode);
          setDefaultWidgetSettings((prevSettings) => ({
            ...prevSettings,
            defaultType: type,
            defaultBackroundColor: theme.backgroundColor,
            defaultFontColor: theme.fontColor,
            defaultButtonColor: theme.buttonColor,
            defaultFont: font,
            defaultShowAdWidget: showAdWidget,
            defaultAdUnitCode: adUnitCode
          }));
        }
      } catch (error) {
        Utils.failureToastMessage(error?.message);
      } finally {
        dispatch(hideLoader());
      }
    };

    fetchWidgetSettings();
  }, [businessID, dispatch]);

  const handleCheckboxChange = (index, value) => {
    setSelectedWidgetType({ index, value });
  };

  const handleShowWidgetAds = () => {
    setShowAdWidget(!showAdWidget);
  };

  const togglePopup = () => {
    setShowPopup(!showPopup);
  };

  const handleColorChange = (color) => {
    const updatedThemeColors = [...themeColors];
    updatedThemeColors[selectedThemeIndex].color = color.hex;
    setThemeColors(updatedThemeColors);
  };

  const toggleDropdown = () => {
    setIsFontDropdownOpen(!isFontDropdownOpen);
    setSearchQuery("");
  };

  const handleFont = (font) => {
    const updatedFont = {
      family: font?.family,
      fileURL: font?.files?.regular
    };
    setSelectedFont(updatedFont);
    setIsFontDropdownOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    let filteredFonts = fonts.filter((font) =>
      font.family.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (selectedFont && searchQuery === "") {
      filteredFonts = [
        selectedFont,
        ...filteredFonts.filter((font) => font.family !== selectedFont.family)
      ];
    }

    setVisibleFonts(filteredFonts.slice(0, page * perPage));
  }, [searchQuery, fonts, page, selectedFont, perPage]);

  return (
    <div className="mt-61px w-full max-w-242">
      <div className="border-grey-D9D9D9 font-MontserratSemiBold text-ft19-23 text-black-131426 md:text-ft22-26.82-0.4">
        Select Widget Type
      </div>
      <div className="mt-4 flex flex-col items-center gap-8 xs:flex-row md:gap-4">
        {widgetType.map((widgetData, index) => (
          <div className="" key={widgetData.name.toLocaleLowerCase()}>
            <div>
              <img
                src={widgetData.image}
                alt="widgetImage"
                className={`${
                  selectedWidgetType.value === widgetData.title
                    ? "rounded-md border-2 border-purple-6C36B0"
                    : ""
                }`}
              />
            </div>
            <div className="mt-4 flex items-center">
              <label className="checkbox-container">
                <input
                  name="consentCheckBox"
                  type="checkbox"
                  checked={selectedWidgetType.value === widgetData.title}
                  onChange={() => handleCheckboxChange(index, widgetData.title)}
                />
                <span className="checkmark"></span>
              </label>
              <div className="ml-6 font-PoppinsMedium text-ft15-18.29-0.3 text-black-000000">
                {widgetData.name}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mb-49px mt-67px flex flex-col items-baseline justify-between gap-12 xs:flex-row md:items-center md:gap-2">
        <div className="w-full">
          <div className="font-MontserratSemiBold text-ft19-23 md:text-ft22-26.82-0.4">
            Widget Theme
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3 md:mt-8.5">
            {themeColors.map((theme, index) => (
              <div
                key={theme?.name.toLocaleLowerCase()}
                className="relative w-auto"
              >
                <div
                  className="flex h-49px cursor-pointer items-center rounded border border-grey-D9D9D9 p-3"
                  onClick={() => setSelectedThemeIndex(index)}
                >
                  <div
                    style={{
                      backgroundColor: theme.color,
                      border:
                        theme.color === "#ffffff" ? "1px solid #DBDBDB" : "none"
                    }}
                    className={
                      "mr-7px size-4 font-MontserratMedium text-ft15-26.25"
                    }
                  />
                  <p className="font-MontserratMedium text-ft15-26.25">
                    {theme.name}
                  </p>
                </div>
                {selectedThemeIndex === index && (
                  <div
                    ref={colorPickerRef}
                    className="absolute left-0 top-61px z-10"
                  >
                    <SketchPicker
                      color={theme.color}
                      onChange={handleColorChange}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="w-full max-w-427px">
          <div className="font-MontserratSemiBold text-ft19-23 md:text-ft22-26.82-0.4">
            Widget Font
          </div>
          <div
            ref={fontDropdownRef}
            className="relative mt-5 cursor-pointer md:mt-8.5"
          >
            <div
              className="z-10 flex h-49px items-center justify-between rounded border border-grey-D9D9D9 px-3 py-4 font-MontserratMedium text-ft15-26.25 text-black-000000"
              onClick={toggleDropdown}
            >
              <span>{selectedFont?.family}</span>
              <img
                className={`transition-transform ${
                  !isFontDropdownOpen ? "" : "rotate-180"
                }`}
                src="/images/dropdown-arrow-purple.svg"
              />
            </div>
            {isFontDropdownOpen && (
              <div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search fonts"
                  className="absolute z-30 mt-1.5 flex h-49px w-full rounded-t border border-grey-C7C7C7 px-4 py-2 font-MontserratRegular text-ft15 outline-none "
                />

                <ul
                  className="absolute z-20 mt-55px h-70 w-full max-w-427px overflow-y-auto rounded-b border border-t-0 border-grey-D9D9D9 bg-white"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#D6D6D6 #FFFFFF"
                  }}
                  onScroll={handleScroll}
                >
                  {visibleFonts.map((font) => (
                    <li
                      key={font?.family.toLocaleLowerCase()}
                      className="cursor-pointer px-4 py-1.5 font-MontserratMedium text-ft15-26.25 text-black-000000 hover:rounded hover:bg-purple-6C36B0 hover:text-white"
                      style={{ fontFamily: `${font?.family}, sans-serif` }}
                      onClick={() => handleFont(font)}
                    >
                      {font.family}
                      <style>
                        {`
                          @font-face {
                            font-family: '${font?.family}';
                            src: url('${font?.files?.regular}');
                          }
                        `}
                      </style>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedWidgetType.value === "POPUP" && (
        <>
          <div className="mb-23px flex flex-row items-center gap-5">
            <div className="font-MontserratSemiBold text-ft22-26.82-0.4 text-black-000000">
              Show Widget Ads
            </div>

            <ToggleSwitch
              toggleSwitch={showAdWidget}
              handleSwitch={handleShowWidgetAds}
            />
          </div>
        </>
      )}
      {selectedWidgetType.value === "POPUP" && showAdWidget && (
        <div className="flex flex-col">
          <div className="font-PoppinsRegular text-ft15-18.29-0.3 text-black-000000">
            Add Ad Unit Code&nbsp;&nbsp;
            <span
              className="cursor-pointer font-PoppinsRegular text-ft15-18.29-0.3 text-purple-6C36B0 "
              onClick={togglePopup}
            >
              What is this?
            </span>
          </div>
          {showPopup && <PopupComponent handleClose={togglePopup} />}
          <input
            type="text"
            value={adUnitCode}
            onChange={(event) => setAdUnitCode(event.target.value)}
            className="mb-19px h-49px rounded border border-grey-D9D9D9 px-3 py-2 focus:outline-none md:w-242"
          />
        </div>
      )}
    </div>
  );
};

export default WidgetComponent;

WidgetComponent.propTypes = {
  setDefaultWidgetSettings: PropTypes.func.isRequired,
  selectedWidgetType: PropTypes.string.isRequired,
  setSelectedWidgetType: PropTypes.func.isRequired,
  themeColors: PropTypes.shape({
    primary: PropTypes.string,
    secondary: PropTypes.string,
    map: PropTypes
  }).isRequired,
  setThemeColors: PropTypes.func.isRequired,
  selectedFont: PropTypes.string.isRequired,
  setSelectedFont: PropTypes.func.isRequired,
  adUnitCode: PropTypes.string.isRequired,
  setAdUnitCode: PropTypes.func.isRequired,
  showAdWidget: PropTypes.bool.isRequired,
  setShowAdWidget: PropTypes.func.isRequired
};
